import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState, } from "react";
import { useNavigate } from "react-router-dom";
import { AVAAIO_LANGUAGES, getInitialPreferredLanguage, normalizeAvaaioLanguage, persistPreferredLanguage, } from "../utils/avaaioLanguage";
import { getAvaaioUiCopy, } from "../utils/avaaioUiCopy";
const API_BASE = import.meta.env.VITE_API_BASE ||
    "https://api.avaaio.com";
function resolveAuthToken() {
    const tokenKeys = [
        "ava_session_token",
        "ava_token",
        "token",
        "access_token",
        "authToken",
        "jwt",
    ];
    for (const key of tokenKeys) {
        const localToken = window.localStorage.getItem(key)?.trim();
        if (localToken) {
            return localToken;
        }
        const sessionToken = window.sessionStorage.getItem(key)?.trim();
        if (sessionToken) {
            return sessionToken;
        }
    }
    return "";
}
function sessionExpiredMessage(language) {
    const messages = {
        en: "Your session has expired. Please sign in again.",
        es: "Tu sesión ha caducado. Inicia sesión nuevamente.",
        fr: "Votre session a expiré. Veuillez vous reconnecter.",
        de: "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
        it: "La sessione è scaduta. Accedi di nuovo.",
        pt: "Sua sessão expirou. Entre novamente.",
    };
    return (messages[language] ||
        messages.en ||
        "Your session has expired. Please sign in again.");
}
export default function Language() {
    const navigate = useNavigate();
    const [selected, setSelected] = useState(getInitialPreferredLanguage());
    const [saved, setSaved] = useState(getInitialPreferredLanguage());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const interfaceCopy = getAvaaioUiCopy(selected);
    const selectedOption = useMemo(() => AVAAIO_LANGUAGES.find((item) => item.code === selected) || AVAAIO_LANGUAGES[0], [selected]);
    useEffect(() => {
        let cancelled = false;
        async function loadPreference() {
            try {
                const token = resolveAuthToken();
                const response = await fetch(`${API_BASE}/api/v1/auth/me`, {
                    credentials: "include",
                    headers: {
                        ...(token
                            ? {
                                Authorization: `Bearer ${token}`,
                            }
                            : {}),
                    },
                });
                const payload = await response
                    .json()
                    .catch(() => ({}));
                if (!response.ok || cancelled) {
                    return;
                }
                const user = payload?.user || payload;
                const language = normalizeAvaaioLanguage(user?.preferred_language);
                setSelected(language);
                setSaved(language);
                persistPreferredLanguage(language);
            }
            catch {
                // The stored local preference remains usable.
            }
            finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }
        void loadPreference();
        return () => {
            cancelled = true;
        };
    }, []);
    async function saveLanguage() {
        if (saving)
            return;
        setSaving(true);
        setMessage("");
        try {
            const token = resolveAuthToken();
            const response = await fetch(`${API_BASE}/api/v1/auth/profile`, {
                method: "PATCH",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...(token
                        ? {
                            Authorization: `Bearer ${token}`,
                        }
                        : {}),
                },
                body: JSON.stringify({
                    preferred_language: selected,
                }),
            });
            const payload = await response
                .json()
                .catch(() => ({}));
            if (response.status === 401 ||
                payload?.error === "missing_token" ||
                payload?.detail === "No token provided") {
                throw new Error(sessionExpiredMessage(selected));
            }
            if (!response.ok || payload?.ok === false) {
                throw new Error(payload?.detail ||
                    payload?.error ||
                    interfaceCopy.language.error);
            }
            const language = persistPreferredLanguage(selected);
            setSaved(language);
            setMessage(interfaceCopy.language.success);
            window.setTimeout(() => {
                navigate("/settings", { replace: true });
            }, 350);
        }
        catch (error) {
            setMessage(error?.message ||
                interfaceCopy.language.error);
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsx("div", { style: styles.page, children: _jsxs("div", { style: styles.shell, children: [_jsx("div", { style: styles.eyebrow, children: interfaceCopy.language.eyebrow }), _jsx("h1", { style: styles.title, children: interfaceCopy.language.title }), _jsx("p", { style: styles.subtitle, children: interfaceCopy.language.subtitle }), _jsxs("div", { style: styles.card, children: [_jsx("label", { htmlFor: "avaaio-account-language", style: styles.label, children: interfaceCopy.language.preferred }), _jsx("select", { id: "avaaio-account-language", value: selected, disabled: loading || saving, onChange: (event) => {
                                setSelected(normalizeAvaaioLanguage(event.target.value));
                                setMessage("");
                            }, style: styles.select, children: AVAAIO_LANGUAGES.map((language) => (_jsx("option", { value: language.code, style: styles.option, children: language.nativeLabel }, language.code))) }), _jsxs("div", { style: styles.selection, children: [interfaceCopy.language.selected, ": ", selectedOption.nativeLabel] }), _jsx("div", { style: styles.note, children: interfaceCopy.language.note }), _jsx("button", { type: "button", onClick: saveLanguage, disabled: loading ||
                                saving ||
                                selected === saved, style: {
                                ...styles.saveButton,
                                opacity: loading ||
                                    saving ||
                                    selected === saved
                                    ? 0.56
                                    : 1,
                                cursor: loading ||
                                    saving ||
                                    selected === saved
                                    ? "not-allowed"
                                    : "pointer",
                            }, children: saving
                                ? interfaceCopy.language.saving
                                : selected === saved
                                    ? interfaceCopy.language.saved
                                    : interfaceCopy.language.save }), message ? (_jsx("div", { style: styles.message, children: message })) : null] })] }) }));
}
const styles = {
    page: {
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        padding: "24px 16px 32px",
        color: "#efffff",
        background: "radial-gradient(circle at top, rgba(0,255,255,0.10), transparent 34%), linear-gradient(180deg, #02050b 0%, #040912 52%, #010203 100%)",
        fontFamily: 'Inter, "Segoe UI", Arial, sans-serif',
    },
    shell: {
        width: "min(100%, 760px)",
        margin: "0 auto",
    },
    eyebrow: {
        color: "#79f3ff",
        fontSize: "0.76rem",
        fontWeight: 800,
        letterSpacing: "0.22em",
        textAlign: "center",
        marginBottom: "10px",
    },
    title: {
        margin: "0 0 10px",
        textAlign: "center",
        fontSize: "clamp(2rem, 7vw, 3rem)",
        lineHeight: 1.08,
    },
    subtitle: {
        margin: "0 auto 22px",
        maxWidth: "650px",
        textAlign: "center",
        color: "rgba(220,245,250,0.80)",
        lineHeight: 1.65,
    },
    card: {
        borderRadius: "24px",
        padding: "24px",
        background: "linear-gradient(180deg, rgba(8,16,28,0.96), rgba(4,9,18,0.98))",
        border: "1px solid rgba(60,220,255,0.22)",
        boxShadow: "0 20px 70px rgba(0,0,0,0.38), 0 0 30px rgba(0,220,255,0.08)",
    },
    label: {
        display: "block",
        marginBottom: "9px",
        color: "#c9fbff",
        fontWeight: 800,
    },
    select: {
        width: "100%",
        minHeight: "56px",
        boxSizing: "border-box",
        borderRadius: "16px",
        border: "1px solid rgba(80,220,255,0.30)",
        background: "#081421",
        color: "#f7fdff",
        WebkitTextFillColor: "#f7fdff",
        colorScheme: "dark",
        padding: "0 16px",
        fontSize: "1rem",
        fontWeight: 700,
        outline: "none",
    },
    option: {
        background: "#081421",
        color: "#f7fdff",
    },
    selection: {
        marginTop: "12px",
        color: "#9cf8ff",
        fontSize: "0.9rem",
        fontWeight: 700,
    },
    note: {
        marginTop: "18px",
        padding: "14px",
        borderRadius: "15px",
        border: "1px solid rgba(100,210,255,0.12)",
        background: "rgba(90,180,255,0.05)",
        color: "rgba(220,242,248,0.78)",
        fontSize: "0.86rem",
        lineHeight: 1.65,
    },
    saveButton: {
        width: "100%",
        minHeight: "54px",
        marginTop: "18px",
        borderRadius: "16px",
        border: "none",
        background: "linear-gradient(90deg, #00d8ff, #00ffee)",
        color: "#03151b",
        fontSize: "1rem",
        fontWeight: 900,
    },
    message: {
        marginTop: "14px",
        textAlign: "center",
        color: "#baffea",
        fontWeight: 700,
        lineHeight: 1.5,
    },
    backButton: {
        width: "100%",
        minHeight: "50px",
        marginTop: "16px",
        borderRadius: "15px",
        border: "1px solid rgba(90,220,255,0.18)",
        background: "rgba(8,16,28,0.90)",
        color: "#b8fbff",
        fontWeight: 800,
        cursor: "pointer",
    },
};
